import React, { useCallback, useEffect, useRef, useState } from 'react'

import NoImageIcon from 'components/icons/NoImageIcon'

import Spinner from './Spinner'

export enum ImageType {
  OBJECT_CONTAIN = 'object-contain',
  OBJECT_COVER = 'object-cover',
  OBJECT_FILL = 'object-fill'
}
type Props = {
  url: string
  name: string
  className?: string
  width?: number
  height?: number
  imageType?: ImageType
  onLoad?: () => void
}

export default function LazyThumbnail({
  url,
  name,
  className,
  width,
  height,
  imageType,
  onLoad
}: Props) {
  const imageRef = useRef<any>()

  const targetUrl = React.useMemo(() => {
    if (!url) return '/assets/no-image.svg'
    if (url.includes('/assets/no-image.svg')) {
      return '/assets/no-image.svg'
    }
    return url
  }, [url])

  const [imageUrl, setImageUrl] = useState('')

  const [online, setOnline] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  const onlineListener = useCallback(() => setOnline(false), [setOnline])

  // Reset imageUrl khi targetUrl prop thay đổi thành giá trị hợp lệ (ví dụ optimistic blob URL
  // được set sau khi component đã render với no-image.svg)
  useEffect(() => {
    if (targetUrl && imageUrl && imageUrl !== targetUrl) {
      setImageUrl('')
      setIsLoaded(false)
    }
  }, [targetUrl, imageUrl])

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setImageUrl(targetUrl)
          observer.disconnect()
        }
      })
    })
    if (imageRef.current && !online && !imageUrl) {
      observer.observe(imageRef.current)
    }
    return () => observer.disconnect()
  }, [imageUrl, online, targetUrl])

  useEffect(() => {
    window.addEventListener('online', onlineListener)

    return () => {
      window.removeEventListener('online', onlineListener)
    }
  }, [onlineListener])

  return (
    <div className={`flex relative ${className} z-0 bg-[#CED0D5]`}>
      {!url ? <Spinner /> : null}
      {online ? (
        <div className="m-auto">
          <NoImageIcon width={width} height={height} />
        </div>
      ) : (
        <img
          crossOrigin="anonymous"
          ref={imageRef}
          className={`w-full h-full transition-all duration-700 ease-in-out ${
            imageType || imageType === ImageType.OBJECT_CONTAIN
              ? imageType
              : 'object-contain'
          } select-none ${imageUrl ? '' : 'invisible'} ${
            isLoaded
              ? 'opacity-100 blur-xs scale-100'
              : 'opacity-40 blur-sm scale-100'
          }`}
          loading="lazy"
          onLoad={() => {
            setIsLoaded(true)
            onLoad?.()
          }}
          onError={() => {
            if (imageUrl) {
              setOnline(true)
              onLoad?.()
            }
          }}
          src={imageUrl}
          alt={name}
          draggable={false}
        />
      )}
    </div>
  )
}

LazyThumbnail.defaultProps = {
  className: 'w-full aspect-[2/1] max-w-[220px]',
  imageType: ImageType.OBJECT_CONTAIN,
  width: 220,
  height: 110,
  onLoad: () => {}
}
